import { useCallback, useEffect, useState } from "react";
import { FaArrowLeft } from "react-icons/fa";
import { api, downloadWithPicker, metadataUrl, type MediaList } from "../api.ts";
import { AddMediaModal } from "../components/AddMediaModal.tsx";
import { MediaTable } from "../components/MediaTable.tsx";
import { StatLabels } from "../components/StatLabels.tsx";
import { OfflineDbPanel } from "../components/OfflineDbPanel.tsx";

export function ProjectPage({ name, orgSlug }: { name: string; orgSlug: string }) {
  const [data, setData] = useState<MediaList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setData(await api.listMedia(name));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [name]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <div className="space-y-6">
      <div>
        <a
          href={`#/o/${encodeURIComponent(orgSlug)}/offline_projects`}
          className="inline-flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-slate-400 transition hover:bg-slate-800 hover:text-slate-100"
        >
          <FaArrowLeft size={15} aria-hidden="true" />
          <span>Offline Sdk</span>
        </a>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-100">{name}</h1>
        </div>
        <div className="flex shrink-0 gap-2">
          <button
            onClick={() =>
              downloadWithPicker(
                metadataUrl(name),
                `${name}-metadata.json`,
              )
            }
            className="rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800"
          >
            Download metadata
          </button>
          <button
            onClick={() => setModalOpen(true)}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500"
          >
            + Add Media
          </button>
        </div>
      </div>

      <StatLabels
        totalFiles={data?.stats.totalFiles ?? 0}
        totalDurationSeconds={data?.stats.totalDuration ?? 0}
        capacityRemainingSeconds={data?.stats.capacityRemaining ?? 600}
      />

      <OfflineDbPanel project={name} mediaCount={data?.stats.totalFiles ?? 0} onMediaChanged={refresh} />

      {error && <p className="text-sm text-rose-400">{error}</p>}

      <MediaTable
        project={name}
        media={data?.media ?? []}
        onRemoved={refresh}
      />

      {modalOpen && (
        <AddMediaModal
          project={name}
          onClose={() => setModalOpen(false)}
          onAdded={() => {
            setModalOpen(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}
